const STORAGE_PREFIX = 'supabase-cache:';
const memoryCache = new Map<string, { value: unknown; expiresAt: number }>();

const DEFAULT_TIMEOUT = 15000; // Increased to 15 seconds for better reliability

export class SupabaseTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SupabaseTimeoutError';
  }
}

export const isSupabaseTimeoutError = (error: unknown): error is SupabaseTimeoutError => {
  return error instanceof SupabaseTimeoutError;
};

type SupabaseErrorLike = {
  message?: string;
  details?: string;
  code?: string | number;
  name?: string;
};

const isAbortLikeSupabaseError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const maybeError = error as SupabaseErrorLike;

  if (maybeError.name === 'AbortError') return true;
  if (maybeError.code === '20' || maybeError.code === 20) return true;

  const combined = `${maybeError.message ?? ''} ${maybeError.details ?? ''}`.toLowerCase();

  return combined.includes('abor');
};

export const isSupabaseAbortError = (error: unknown): boolean => {
  if (isSupabaseTimeoutError(error)) return true;
  if (isAbortLikeSupabaseError(error)) return true;
  return false;
};

const extractSupabaseResponseError = (value: unknown): SupabaseErrorLike | null => {
  if (!value || typeof value !== 'object') return null;

  if ('error' in (value as Record<string, unknown>)) {
    const maybeError = (value as { error?: unknown }).error;
    if (!maybeError) {
      return null;
    }

    if (typeof maybeError === 'object') {
      return maybeError as SupabaseErrorLike;
    }

    return {
      message: String(maybeError),
    };
  }

  return null;
};

const hasSupabaseResponseError = (value: unknown): boolean => {
  return Boolean(extractSupabaseResponseError(value));
};

const isAbortError = (error: unknown) => {
  if (!error) return false;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error && error.name === 'AbortError') return true;
  if (error instanceof Error && error.message?.toLowerCase().includes('abort')) return true;

  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      message?: string;
      details?: string;
      code?: string | number;
    };

    const combinedMessage = `${maybeError.message ?? ''} ${maybeError.details ?? ''}`.toLowerCase();

    if (combinedMessage.includes('abort')) return true;
    if (combinedMessage.includes('aborted')) return true;
    if (maybeError.code === '20' || maybeError.code === 20) return true;
  }

  return false;
};

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (error) {
    console.error('LocalStorage access error:', error);
    return null;
  }
};

export interface SupabaseQueryOptions {
  cacheKey?: string;
  cacheTtlMs?: number;
  timeoutMs?: number;
  forceRefresh?: boolean;
}

export function getCachedValue<T>(key: string): T | null {
  const now = Date.now();
  const memoryEntry = memoryCache.get(key);
  if (memoryEntry && memoryEntry.expiresAt > now) {
    const cachedValue = memoryEntry.value as T;
    if (hasSupabaseResponseError(cachedValue)) {
      memoryCache.delete(key);
    } else {
      return cachedValue;
    }
  }

  const storage = getStorage();
  if (!storage) return null;

  const raw = storage.getItem(`${STORAGE_PREFIX}${key}`);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { value: T; expiresAt: number };
    if (parsed.expiresAt > now) {
      if (hasSupabaseResponseError(parsed.value)) {
        storage.removeItem(`${STORAGE_PREFIX}${key}`);
        return null;
      }

      memoryCache.set(key, {
        value: parsed.value,
        expiresAt: parsed.expiresAt,
      });
      return parsed.value;
    }
    storage.removeItem(`${STORAGE_PREFIX}${key}`);
  } catch (error) {
    console.error('Failed to parse cached value:', error);
    storage.removeItem(`${STORAGE_PREFIX}${key}`);
  }

  return null;
}

export function setCachedValue<T>(key: string, value: T, ttlMs: number) {
  if (hasSupabaseResponseError(value)) {
    invalidateCachedValue(key);
    return;
  }

  const expiresAt = Date.now() + ttlMs;
  memoryCache.set(key, { value, expiresAt });

  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify({ value, expiresAt }));
  } catch (error) {
    console.warn('Failed to persist cache to storage:', error);
  }
}

export function invalidateCachedValue(key: string) {
  memoryCache.delete(key);

  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(`${STORAGE_PREFIX}${key}`);
  } catch (error) {
    console.warn('Failed to remove cached value from storage:', error);
  }
}

export function invalidateCachedValuesByPrefix(prefix: string) {
  for (const key of memoryCache.keys()) {
    if (key.startsWith(prefix)) {
      memoryCache.delete(key);
    }
  }

  const storage = getStorage();
  if (!storage) return;

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const storageKey = storage.key(i);
      if (storageKey && storageKey.startsWith(`${STORAGE_PREFIX}${prefix}`)) {
        keysToRemove.push(storageKey);
      }
    }

    for (const storageKey of keysToRemove) {
      storage.removeItem(storageKey);
    }
  } catch (error) {
    console.warn('Failed to remove cached values by prefix from storage:', error);
  }
}

export async function runSupabaseQuery<T>(
  // PostgREST builders are thenable rather than real promises, so the factory is typed
  // against PromiseLike to let call sites hand the builder straight over.
  factory: (signal: AbortSignal) => PromiseLike<T>,
  options: SupabaseQueryOptions = {},
): Promise<T> {
  const { cacheKey, cacheTtlMs = 5 * 60 * 1000, timeoutMs = DEFAULT_TIMEOUT, forceRefresh = false } = options;

  if (cacheKey && !forceRefresh) {
    const cached = getCachedValue<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const result = await factory(controller.signal);

    const responseError = extractSupabaseResponseError(result);
    if (responseError) {
      if (cacheKey) {
        invalidateCachedValue(cacheKey);
      }

      if (isAbortLikeSupabaseError(responseError)) {
        throw new SupabaseTimeoutError(`Supabase request timed out after ${timeoutMs}ms`);
      }
    } else if (cacheKey) {
      setCachedValue(cacheKey, result, cacheTtlMs);
    }

    return result;
  } catch (error) {
    if (isAbortError(error)) {
      throw new SupabaseTimeoutError(`Supabase request timed out after ${timeoutMs}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function withTimeout<T>(
  promiseFactory: () => Promise<T>,
  timeoutMs = DEFAULT_TIMEOUT,
  message?: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new SupabaseTimeoutError(message ?? `Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promiseFactory(), timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}
